import React, { useCallback, useEffect, useState } from "react";
import { Checkbox, GridLegacy, IconButton, TableCell, TextField, Tooltip, Typography } from "@mui/material";
import TranslationWrapper from "../../components/TranslationWrapper/TranslationWrapper";
import MainNavbar from "../../components/Navbars/MainNavbar";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import AuthenticationWrapper from "../../auth/AuthenticationWrapper";
import { withStyles } from "tss-react/mui";
import vestigingStyle from "../../assets/jss/material-dashboard-pro-react/views/vestigingStyle";
import { Add, Delete, Layers, Visibility } from "@mui/icons-material";
import CreateNewProfileDialog from "./CreateNewProfileDialog";
import JamezzTableV2, { useReloadState } from "../../components/JamezzTable/V2/JamezzTableV2";
import FormElement from "../../components/JamezzTable/FormElement";
import Person from "@mui/icons-material/Person";
import { Link } from "react-router";
import Edit from "@mui/icons-material/Edit";
import Button from "../../components/CustomButtons/Button";
import DuplicateProfileDialog from "./DuplicateProfileDialog";
import useJTFilter from "../../components/JamezzTable/V2/useJTFilter";
import { _post } from "../../http";
import HelpPopover from "../../components/Popover/HelpPopover";
import AreYouSureDialog from "../../components/Dialogs/AreYouSureDialog";
import { useDialog } from "../../licensing/utils/DialogProvider";

const endPoint = "core/profiles";

function Profiles({ classes }) {
  const [newProfileDialogIsOpen, setNewProfileDialogIsOpen] = useState(false);
  const [duplicateProfile, setDuplicateProfile] = useState(false);
  const [openDialog] = useDialog();
  const { reloadState, updateReloadState } = useReloadState();
  const [textField, filters] = useJTFilter("name");
  const [profileTypes, setProfileTypes] = useState(null);

  useEffect(() => {
    _post("core/profileTypes/search", {}, { showSpinner: true, showToastSuccess: false }).then((response) => {
      const profileTypesArray = response.data.data.data;
      const profileTypes = {};

      let higestRank = null; // High is low
      profileTypesArray.forEach((profileType) => {
        if (higestRank == null || higestRank > profileType.rank) {
          higestRank = profileType.rank;
        }
        profileTypes[profileType.id] = profileType;
      });

      profileTypesArray.forEach((profileType) => {
        profileType.rank -= higestRank;
      });

      setProfileTypes(profileTypes);
    });
  }, []);

  const renderHeaderCells = useCallback(
    () => [
      { id: "id", label: "#", numeric: true, disablePadding: false, sortable: false },
      {
        id: "name",
        label: <TranslationWrapper tDefault={"Profile name"} tKey={"profiles.name"} />,
        numeric: false,
        disablePadding: false,
        sortable: false,
      },
      {
        id: "name",
        label: <TranslationWrapper tDefault={"Profile type"} tKey={"profiles.type"} />,
        numeric: false,
        disablePadding: false,
        sortable: false,
      },
      {
        id: "name",
        label: <TranslationWrapper tDefault={"Jamezz Global"} tKey={"profiles.isGlobal"} />,
        numeric: false,
        disablePadding: false,
        sortable: false,
      },
    ],
    []
  );

  const renderRowCells = useCallback(
    (row, isEditable) => {
      return (
        <>
          <TableCell align={"right"} style={{ width: 100 }}>
            {row.id}
          </TableCell>
          <TableCell>
            <div
              style={{
                marginLeft: profileTypes?.[row.profile_type_id] ? profileTypes[row.profile_type_id].rank * 8 : 0,
              }}
            >
              {isEditable ? (
                <FormElement endPoint={"core/profiles"} name={"name"} row={row}>
                  <TextField size={"small"} variant={"outlined"} />
                </FormElement>
              ) : null}
              {!isEditable ? row.name : null}
            </div>
          </TableCell>
          <TableCell>
            <Typography style={{ fontWeight: 800 }}>{profileTypes?.[row.profile_type_id].name}</Typography>
          </TableCell>
          <TableCell>
            <Checkbox checked={row.user_id == null} color={"primary"} disabled={true} style={{ padding: 0 }} />
          </TableCell>
        </>
      );
    },
    [profileTypes]
  );

  const renderSelectionActions = useCallback((selected) => {
    return (
      <Tooltip title={<TranslationWrapper tKey={"Delete"} />}>
        <IconButton
          onClick={() => {
            openDialog({
              children: (
                <AreYouSureDialog
                  content={<TranslationWrapper tKey={"Are you sure you want to delete the selected profiles?"} />}
                  onOk={() => {
                    _post("/core/profiles/multipledelete", { ids: selected }).then(() => {
                      updateReloadState();
                    });
                  }}
                />
              ),
            });
          }}
          style={{ color: "lightcoral" }}
        >
          <Delete />
        </IconButton>
      </Tooltip>
    );
  }, []);

  const renderRowActions = useCallback((row) => {
    return (
      <TableCell align={"right"}>
        {row.user_id != null ? (
          <Link to={`./${row.id}/edit`}>
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              placement={"bottom"}
              title={<TranslationWrapper tDefault={"Edit"} tKey={"global.jamezzTable.edit"} />}
            >
              <IconButton color={"primary"} simple size={"small"}>
                <Edit />
              </IconButton>
            </Tooltip>
          </Link>
        ) : null}
        {row.user_id == null ? (
          <Link to={`./${row.id}/edit`}>
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              placement={"bottom"}
              title={<TranslationWrapper tDefault={"View"} tKey={"global.jamezzTable.view"} />}
            >
              <IconButton color={"primary"} simple size={"small"}>
                <Visibility />
              </IconButton>
            </Tooltip>
          </Link>
        ) : null}

        <AuthenticationWrapper permissionKey={"profiles.duplicate"}>
          <Tooltip
            classes={{ tooltip: classes.tooltip }}
            placement={"bottom"}
            title={<TranslationWrapper tDefault={"Duplicate"} tKey={"vestigingen.table.tooltip.dupliceer"} />}
          >
            <IconButton
              color={"primary"}
              onClick={() => {
                setDuplicateProfile(row);
              }}
              simple
              size={"small"}
            >
              <Layers />
            </IconButton>
          </Tooltip>
        </AuthenticationWrapper>
      </TableCell>
    );
  }, []);

  const sort = useCallback(
    (a, b) => {
      if (!profileTypes) {
        return 0;
      }
      const rankDif = profileTypes[a.profile_type_id].rank - profileTypes[b.profile_type_id].rank;
      if (rankDif !== 0) {
        return rankDif;
      } else {
        return a.id - b.id;
      }
    },
    [profileTypes]
  );

  return (
    <div>
      <MainNavbar paginaTitel={<TranslationWrapper tDefault={"Profiles"} tKey={"profiles.table.title"} />} />
      <GridContainer>
        <GridItem xs={12}>
          <AuthenticationWrapper
            child={
              <Button
                className={classes.marginRight}
                color={"success"}
                onClick={() => {
                  setNewProfileDialogIsOpen(true);
                }}
              >
                <Add className={classes.icons} />{" "}
                <TranslationWrapper tDefault={"Create new profile"} tKey={"profiles.createNewProfile"} />
              </Button>
            }
            permissionKey={"profiles.create"}
          />
        </GridItem>
      </GridContainer>
      <GridLegacy container spacing={1}>
        <GridLegacy container item justifyContent={"space-between"} xs={12}>
          {textField}
          <HelpPopover
            text={
              "User profiles are composed in this section of the backoffice. These profiles give users permissions to access sections of the backoffice. A few standard (global) profiles are already created for you. If these global profiles do not fit your case, you can duplicate one to create your own profile."
            }
            title={"User profiles"}
          />
        </GridLegacy>
        <GridLegacy item xs={12}>
          {profileTypes ? (
            <JamezzTableV2
              checkboxSelection={true}
              defaultRowsPerPage={100}
              dense={true}
              endPoint={"core/profiles"}
              filters={filters}
              headerIcon={<Person />}
              reloadState={reloadState}
              renderCellsForRow={renderRowCells}
              renderHeaderCells={renderHeaderCells}
              renderRowActions={renderRowActions}
              renderSelectionActions={renderSelectionActions}
              rowIsEditable={() => false}
              rowIsSelectable={(row) => row.user_id != null}
              rowsPerPageOptions={[100]}
              sort={sort}
              tableTitle={<TranslationWrapper tDefault={"Profiles"} tKey={"profiles.table.title"} />}
              uuid={"Profiles-243rukx3"}
            />
          ) : null}
        </GridLegacy>
      </GridLegacy>
      {newProfileDialogIsOpen ? (
        <CreateNewProfileDialog
          onClose={() => {
            setNewProfileDialogIsOpen(false);
          }}
        />
      ) : null}
      {duplicateProfile ? (
        <DuplicateProfileDialog
          onClose={() => {
            setDuplicateProfile(false);
          }}
          profile={duplicateProfile}
        />
      ) : null}
    </div>
  );
}

export default withStyles(Profiles, vestigingStyle);
